/**
 * Session revocation check — CU-86akkg3gu Part 3.
 *
 * ── THE PROBLEM ───────────────────────────────────────────────────────────
 *
 * When a patient's credentials are revoked — password change, emailed reset,
 * admin Reset MFA, passkey revoke-all, email change — their LIVE session keeps
 * full PHI read AND write access until the access token expires. Measured at 41
 * and 48 minutes; the bound is **up to ~60 minutes from the last token refresh**.
 *
 * The session survives for TWO independent reasons, and fixing either alone is
 * not enough: (1) the token has not expired, and (2) **nothing re-asks**. This
 * module addresses (2).
 *
 * ── WHY THIS WORKS — MEASURED, NOT INFERRED ───────────────────────────────
 *
 * Staging 2026-09-22, patient `i68uE7MWOGMuK2Qoupk1wdLkinY2` (passkey-only).
 * Full record: `docs/projects/CU-86akjurnv/CU-86akjurnv_PROBE_FORCE_REFRESH.md`
 * Addenda 7 and 9.
 *
 *   | Session state | getIdToken(true) | Code                        | Elapsed | Signs out? |
 *   |---------------|------------------|-----------------------------|---------|------------|
 *   | Valid         | resolves         | —                           | ~100 ms | no         |
 *   | Offline       | rejects          | auth/network-request-failed |    3 ms | **no**     |
 *   | Revoked       | rejects          | auth/user-token-expired     |   99 ms | **yes**    |
 *
 * ⚠️ **THE SIGN-OUT IS NOT OURS.** A rejected refresh makes the Firebase SDK
 * fire `onAuthStateChanged(null)` ITSELF, which `AuthContext.tsx:79` observes and
 * `_authenticated.tsx` turns into a redirect to `/login`. That chain was captured
 * in the probe's own stack trace. **So this module only has to ASK.**
 *
 * ⚠️ **THEREFORE: NEVER CALL signOut() FROM HERE.** Doing so would add a second,
 * unmeasured sign-out path racing the SDK's — and it would be reached by the
 * offline branch, which must stay inert. Ask, classify, report. Nothing else.
 *
 * ── ⚠️ WHY NOT `getAuthAssurance(true)` ───────────────────────────────────
 *
 * It already forces a refresh (`useMfaRouting.ts:76`), but its catch SWALLOWS the
 * rejection and returns `{ aal2: false }` — deliberately, so a transient read
 * failure cannot masquerade as a real answer (`authAssurance.ts:104`). Through
 * that wrapper **revoked is indistinguishable from offline**, and a revoked
 * patient would be routed to ENROLMENT rather than signed out. This module calls
 * `getIdToken(true)` directly for exactly that reason. **Do not "consolidate"
 * them.**
 *
 * ── WHAT THIS REPLACED ────────────────────────────────────────────────────
 *
 * Six mechanisms were investigated 2026-09-22; five were eliminated. Do not
 * re-propose them without reading Addendum 8:
 *
 *   - Auth event triggers — Gen 1 only (create/delete), and Gen 2 drops them
 *   - `onUserPasswordChanged` / `beforeEachUserUpdated` — **DO NOT EXIST**
 *   - `beforeEmailSent` — fires on SEND, not completion; would eject anyone who
 *     merely clicks "Forgot password"
 *   - Cloud Audit Logs — **nothing** logged for a patient-completed reset
 *   - Server poll on `passwordUpdatedAt` — cannot tell WHICH session is stale, so
 *     it would eject a patient who just signed back in
 *   - Mirror write to `mfa.*` — reader dep compared equal (Addendum 3)
 *
 * This approach is indifferent to HOW the revocation happened, because Firebase
 * adjudicates. That includes the Firebase Console path, which no server-side seam
 * can observe.
 */

import * as Sentry from '@sentry/browser';
import { auth } from '../../constants/firebaseModular';
import { scrubSafeAuthCode } from './sentryAuthCode';

/** Closed union — no free text reaches Sentry from this module. */
export type RevocationCheckOutcome =
  | 'ok' // refresh succeeded — session still valid
  | 'revoked' // refresh refused — the SDK is signing them out
  | 'offline' // transport failure — MUST stay inert
  | 'throttled' // rate limited — see the note on EXPECTED_AUTH_CODES below
  | 'no-user' // nobody signed in
  | 'suspended' // this tab is mid credential-change — see the grace window
  | 'unknown'; // rejected with a code we did not predict

/**
 * ⚠️ MEASURED: `auth/user-token-expired` is what a revoked session actually
 * returns (Addendum 7). The others are defensive — they are genuine
 * credential-invalid states where continuing to serve PHI would be wrong.
 *
 * ⚠️ `auth/requires-recent-login` is deliberately ABSENT. It means "re-auth to
 * perform a sensitive action", NOT "your session is dead", and treating it as
 * revoked would sign out a patient whose session is perfectly valid.
 */
const REVOKED_CODES = new Set([
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/invalid-user-token',
]);

/**
 * ⚠️ THE SAFETY BRANCH. A patient on a train is not a revoked session.
 *
 * Measured (Addendum 9): four sustained offline checks, including an unattended
 * timer firing, all classified here and **left the session alone**. Recovery to
 * `ok` was immediate on reconnect.
 *
 * ⚠️ A codeless transport failure was MEASURED on the passkey leg 2026-08-06, so
 * no site may rely on `code` alone. An unrecognised rejection lands in `unknown`,
 * which does NOT sign anyone out — the fail-safe direction.
 */
const OFFLINE_CODES = new Set(['auth/network-request-failed']);

/**
 * ⚠️ SEPARATE FROM `unknown` ON PURPOSE — this is the rate-limit signal.
 *
 * Unpublished quota on `securetoken.googleapis.com` is the one input that could
 * not be measured before shipping. Giving throttling its own outcome makes it
 * greppable in Sentry as `check_outcome:throttled` rather than hiding inside a
 * general bucket.
 *
 * ⚠️ AND IT MUST NOT BE READ AS REVOCATION. Being throttled says nothing about
 * whether the session is valid, so it is inert like `offline`. If this fires at
 * volume, LENGTHEN THE BACKSTOP — do not start signing people out on it.
 *
 * ⚠️ NOTE `App.tsx:54` — `auth/too-many-requests` is in `EXPECTED_AUTH_CODES`,
 * which downgrades matching EXCEPTIONS to `info`. This module emits
 * `captureMessage` with its own tags, so that downgrade does not apply here and
 * the signal survives.
 */
const THROTTLED_CODES = new Set(['auth/too-many-requests', 'auth/quota-exceeded']);

function classify(code: string): RevocationCheckOutcome {
  if (REVOKED_CODES.has(code)) return 'revoked';
  if (OFFLINE_CODES.has(code)) return 'offline';
  if (THROTTLED_CODES.has(code)) return 'throttled';
  return 'unknown';
}

/**
 * ⚠️ ONLY `revoked` AND `unknown` ARE REPORTED.
 *
 * `ok` is the overwhelming majority of checks and reporting it would emit one
 * event per session per interval, forever — a self-inflicted flood for zero
 * diagnostic value. The PROBE reported every outcome because it was measuring
 * the instrument; production is not.
 *
 * `offline` is likewise expected and routine.
 *
 * `throttled` IS reported — it is the quota signal, and it should be rare.
 */
const REPORTED_OUTCOMES = new Set<RevocationCheckOutcome>([
  'revoked',
  'throttled',
  'unknown',
]);

/**
 * ⚠️ THE DELIBERATE-CREDENTIAL-CHANGE WINDOW — MEASURED, NOT HYPOTHETICAL.
 *
 * A patient changing their OWN password moves `validSince` server-side. The SDK
 * mints a fresh token, but for a moment the old one is still in flight, and a
 * check landing in that gap sends the STALE token to Firebase, which correctly
 * rejects it. The SDK then fires `onAuthStateChanged(null)` and the patient is
 * signed out — **one second after successfully changing their password.**
 *
 * ⚠️ MEASURED ON STAGING 2026-09-23, patient `8hr1vVU57Jawlr6T0qDZMuGZ2eq1`:
 *
 *     15:34:12  password changed, validSince moves
 *     15:34:13  focus-triggered check -> user-token-expired -> SIGNED OUT
 *
 * That is the too-eager sign-out this whole feature exists to avoid, in its
 * worst form: triggered by the patient doing exactly the right thing. Changing a
 * password involves clicking between fields and (for an SMS holder) a challenge
 * screen, so `focus` and `route` triggers fire readily in that window.
 *
 * ⚠️ THIS IS NOT A WEAKENING OF THE CONTROL. A rejection here is not evidence of
 * revocation — it is the expected churn of a credential change the patient
 * initiated in this tab. A genuine revocation still lands on the very next
 * check after the suspension lifts, which is seconds away.
 *
 * ⚠️ TIME-BOXED, AND DELIBERATELY SHORT. An open-ended suspension would be a
 * hole: anything that forgot to clear it would silently disable the control. It
 * expires on its own.
 */
const CREDENTIAL_CHANGE_GRACE_MS = 10_000;

let suspendedUntil = 0;

/**
 * Suspend revocation checks briefly around a credential change this tab is
 * performing itself.
 *
 * ⚠️ CALL THIS ONLY FOR A CHANGE THE PATIENT INITIATED HERE. It must never wrap
 * a revocation performed elsewhere — that is precisely what the check is for.
 */
export function suspendRevocationChecks(): void {
  suspendedUntil = Date.now() + CREDENTIAL_CHANGE_GRACE_MS;
}

/** Exported for tests — module state would otherwise leak between them. */
export function __resetSuspension(): void {
  suspendedUntil = 0;
}

/**
 * Ask Firebase whether this session is still valid.
 *
 * @param trigger why this ran — `focus`, `route`, `backstop` or `startup`.
 * @returns the classified outcome. Callers may ignore it; the sign-out on a
 *   genuine revocation is performed by the SDK, not by anything here.
 */
export async function checkSessionRevocation(
  trigger: string
): Promise<RevocationCheckOutcome> {
  // ⚠️ BEFORE `auth.currentUser` — the whole point is to not ask at all.
  if (Date.now() < suspendedUntil) return 'suspended';

  const user = auth.currentUser;
  if (!user) return 'no-user';

  const startedAt = performance.now();
  try {
    await user.getIdToken(true);
    return 'ok';
  } catch (error) {
    const code = (error as { code?: string })?.code ?? '';
    const outcome = classify(code);

    if (REPORTED_OUTCOMES.has(outcome)) {
      // ⚠️ TAGS, NOT `extra`. Sentry aggregates on tags, and it REDACTS any
      // extra/contexts value containing "auth" — a raw Firebase code in `extra`
      // arrives `[Filtered]`. Hence `scrubSafeAuthCode` + a tag.
      Sentry.captureMessage(`session-revocation-check: ${outcome}`, {
        level: outcome === 'revoked' ? 'info' : 'warning',
        tags: {
          feature: 'session-revocation',
          // ⚠️ Distinguishes the two clients within one `feature` tag. Mobile
          // sets `platform_client: 'mobile'`; without this the portal's events
          // are indistinguishable from mobile's in a combined search.
          platform_client: 'portal',
          check_outcome: outcome,
          check_code: scrubSafeAuthCode(code),
          check_trigger: trigger,
          // Bucketed — a tag with unbounded cardinality is unusable. Offline
          // fails at ~3 ms and a real exchange takes ~100 ms (Addendum 9), so
          // this independently cross-checks the code.
          check_latency:
            performance.now() - startedAt < 20 ? 'local' : 'round-trip',
        },
      });
    }

    return outcome;
  }
}

/**
 * ⚠️ THE BACKSTOP IS THE WORST CASE, NOT THE TYPICAL ONE.
 *
 * Detection latency ≈ time to the next check, so the EVENT triggers do the real
 * work: a patient who tabs away and returns is checked on focus, and moving to
 * any PHI surface is checked on navigation. The backstop only governs a tab
 * sitting untouched in the foreground — the lowest-risk state, because nobody is
 * looking at it.
 *
 * ⚠️ TUNING THE BACKSTOP BUYS LESS THAN GETTING THE EVENT TRIGGERS RIGHT.
 * Agreed at 5 minutes (2026-09-23): a 12× improvement on the ~60-minute window
 * at negligible cost (~100 ms, one request, no Firestore read, no function
 * invocation). If `check_outcome:throttled` appears at volume, LENGTHEN THIS —
 * do not compensate elsewhere.
 */
export const BACKSTOP_INTERVAL_MS = 5 * 60 * 1000;
